import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, Send, TrendingUp, Eye, MessageCircle, Globe, Sparkles, Zap, BarChart3, Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/stats").then((r) => setStats(r.data)),
      api.get("/leads").then((r) => setRecent(r.data.slice(0, 5))),
    ]).catch(() => {});
  }, []);

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">— Overview</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-[#5C5C66] mt-2">Your pipeline at a glance.</p>
        </div>
        <Link to="/app/campaigns" data-testid="overview-new-campaign">
          <Button className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white font-medium shadow-soft">
            New campaign <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Stat icon={Users} label="Prospects" value={stats?.total_leads ?? 0} />
        <Stat icon={Target} label="Campaigns" value={stats?.campaigns ?? 0} />
        <Stat icon={Send} label="Outreach drafts" value={stats?.emails_generated ?? 0} />
        <Stat icon={TrendingUp} label="Avg ICP score" value={stats?.avg_score ?? 0} accent />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <RateStat icon={Send} label="Sent" value={stats?.sent ?? 0} suffix="" />
        <RateStat icon={TrendingUp} label="Open rate" value={`${stats?.open_rate ?? 0}%`} accent="green" />
        <RateStat icon={MessageCircle} label="Reply rate" value={`${stats?.reply_rate ?? 0}%`} accent="orange" />
        <RateStat icon={Eye} label="Bounces" value={stats?.bounced ?? 0} accent={stats?.bounced ? "red" : null} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="md:col-span-2 bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
          <div className="p-5 border-b border-[#EAEAE3] flex items-center justify-between">
            <div className="font-display text-lg font-bold">Recent prospects</div>
            <Link to="/app/leads" className="text-xs text-[#5C5C66] font-medium hover:text-[#0A0A0F] transition-colors" data-testid="view-all-leads">View all →</Link>
          </div>
          <div>
            {recent.length === 0 ? (
              <div className="p-12 text-center text-[#5C5C66] text-sm">
                No prospects yet. <Link to="/app/campaigns" className="text-[#0A0A0F] underline underline-offset-4 hover:text-[#002FA7]">Create your first campaign</Link>.
              </div>
            ) : recent.map((l) => (
              <Link key={l.id} to={`/app/leads/${l.id}`} className="flex items-center justify-between px-5 py-3 border-b border-[#EAEAE3] hover:bg-[#FAFAF7] transition-colors last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#1E4FD6] text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {l.name.split(" ").map(w => w[0]).join("").slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{l.name}</div>
                    <div className="text-xs text-[#5C5C66] truncate">{l.title}{l.company ? ` · ${l.company}` : ""}</div>
                  </div>
                </div>
                <div className="font-mono text-sm font-bold text-[#002FA7]">{l.score}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-[#0A0A0F] text-white p-6 rounded-2xl relative overflow-hidden shadow-soft">
          <div className="absolute inset-0 dot-bg-dark opacity-50" />
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#002FA7]/30 blur-3xl" />
          <div className="relative">
            <div className="label-mono !text-white/50 mb-2">— Pipeline status</div>
            <div className="font-display text-3xl font-black tracking-tighter mb-6">{stats?.qualified ?? 0} <span className="text-white/60 text-2xl">qualified</span></div>
            <div className="space-y-3 text-sm">
              <Row label="New" value={Math.max(0, (stats?.total_leads ?? 0) - (stats?.contacted ?? 0) - (stats?.qualified ?? 0))} />
              <Row label="Contacted" value={stats?.contacted ?? 0} />
              <Row label="Replied" value={stats?.replied ?? 0} />
              <Row label="Qualified" value={stats?.qualified ?? 0} highlight />
            </div>
          </div>
        </div>
      </div>

      {/* Quick-access tools */}
      <div className="mt-5">
        <div className="label-mono mb-3">— Lead generation tools</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { to: "/app/scraper", icon: Globe, label: "Domain Scraper", desc: "Find decision-makers", color: "from-[#002FA7] to-[#1E4FD6]" },
            { to: "/app/enrich", icon: Sparkles, label: "Enrich CSV", desc: "Fill missing data", color: "from-[#7C3AED] to-[#6D28D9]" },
            { to: "/app/triggers", icon: Zap, label: "Trigger Events", desc: "Funding & hiring", color: "from-[#FF5722] to-[#E64A19]" },
            { to: "/app/sequences", icon: Mail, label: "Sequences", desc: "Drip outreach", color: "from-[#0D9488] to-[#0F766E]" },
            { to: "/app/analytics", icon: BarChart3, label: "Analytics", desc: "Track performance", color: "from-[#0A0A0F] to-[#14141B]" },
          ].map((tool) => (
            <Link key={tool.to} to={tool.to}
              className="bg-white border border-[#EAEAE3] rounded-2xl p-4 shadow-soft hover:shadow-soft-md transition-all hover:-translate-y-0.5 group">
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center mb-3`}>
                <tool.icon className="h-4 w-4 text-white" />
              </div>
              <div className="font-medium text-sm group-hover:text-[#002FA7] transition-colors">{tool.label}</div>
              <div className="text-xs text-[#9A9AA3] mt-0.5">{tool.desc}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft hover:shadow-soft-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent ? "bg-[#002FA7]/10 text-[#002FA7]" : "bg-[#F4F4EE] text-[#0A0A0F]"}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="label-mono">{label}</div>
      </div>
      <div className="font-display text-3xl font-black tracking-tighter">{value}</div>
    </div>
  );
}

function RateStat({ icon: Icon, label, value, accent }) {
  const tone = accent === "green" ? "text-[#16A34A] bg-green-50" :
               accent === "orange" ? "text-[#FF5722] bg-[#FF5722]/10" :
               accent === "red" ? "text-red-600 bg-red-50" :
               "text-[#0A0A0F] bg-[#F4F4EE]";
  return (
    <div className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="label-mono">{label}</div>
      </div>
      <div className="font-display text-3xl font-black tracking-tighter">{value}</div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-2 last:border-0 last:pb-0">
      <span className="text-white/60">{label}</span>
      <span className={`font-mono font-bold ${highlight ? "text-[#FF5722]" : ""}`}>{value}</span>
    </div>
  );
}
